/**
 * Pure helpers for pruning orphaned entries from `user_overrides` and
 * `transcripts` before they are sent to the backend.
 *
 * Why: both maps are keyed by ids that come from luminary (scene id and
 * scene-group id respectively), but nothing removes entries when the
 * scene/scene-group they reference is deleted. Over time they accumulate
 * — a production video was observed with 9,172 orphaned user_overrides
 * entries and 485 orphaned transcripts entries (~33 MB decompressed).
 */

type LiveIdSource = {
    scenes: ReadonlyArray<{ id: string }>;
    sceneGroups: ReadonlyArray<{ id: string }>;
};

export function pruneOrphanedUserOverrides<T>(
    userOverrides: Record<string, T>,
    liveSceneIds: ReadonlySet<string>,
): Record<string, T> {
    const pruned: Record<string, T> = {};
    for (const sceneId of Object.keys(userOverrides)) {
        if (liveSceneIds.has(sceneId)) {
            pruned[sceneId] = userOverrides[sceneId];
        }
    }
    return pruned;
}

export function pruneOrphanedTranscripts<T>(
    transcripts: Record<string, T>,
    liveSceneGroupIds: ReadonlySet<string>,
): Record<string, T> {
    const pruned: Record<string, T> = {};
    for (const sceneGroupId of Object.keys(transcripts)) {
        if (liveSceneGroupIds.has(sceneGroupId)) {
            pruned[sceneGroupId] = transcripts[sceneGroupId];
        }
    }
    return pruned;
}

/**
 * Prune both dicts against a luminary instance's live scene and scene-group
 * ids. Call this at save-time so that any orphaned entries (from scenes or
 * scene-groups that have since been removed) are not persisted.
 */
export function pruneForSave<U, T>(
    {
        userOverrides,
        transcripts,
    }: {
        userOverrides: Record<string, U>;
        transcripts: Record<string, T>;
    },
    luminary: LiveIdSource,
): {
    userOverrides: Record<string, U>;
    transcripts: Record<string, T>;
} {
    const liveSceneIds = new Set(luminary.scenes.map((s) => s.id));
    const liveSceneGroupIds = new Set(luminary.sceneGroups.map((sg) => sg.id));
    return {
        userOverrides: pruneOrphanedUserOverrides(userOverrides, liveSceneIds),
        transcripts: pruneOrphanedTranscripts(transcripts, liveSceneGroupIds),
    };
}
